import React, { FunctionComponent, MouseEvent, useState } from 'react';
import { KeyboardArrowDown, KeyboardArrowUp, HelpOutline, MailOutline } from '@mui/icons-material';
import { Alert, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import useTypedSelector from 'hooks/useTypedSelector';
import i18next from 'i18next';

import { COLORS } from 'helpers/enums/colors';
import { history } from 'helpers/history';

import { INavbarProps } from './types';
import {
  NavbarContainer,
  NavbarContent,
  NavbarMenuButton,
  NavbarAccountButton,
  NavbarProfileAvatar,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuItemLabel,
  NavbarNavlinkContainer,
  NavbarNavlinkButton,
  NavbarLogoutButton,
  NavbarHelpButton,
  WhatsAppMenuIcon,
} from './styles';
import './i18n';

const Navbar: FunctionComponent<INavbarProps> = (props: INavbarProps) => {
  const {
    initials,
    isMobile,
    isPublic,
    items,
    onLogoutPublicClick,
    onOpenMenu,
    title,
    isVerified,
  } = props;
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const EndIcon = !menuElement ? KeyboardArrowDown : KeyboardArrowUp;
  const [helpAnchor, setHelpAnchor] = useState<null | HTMLElement>(null);

  const whatsappNumber = useTypedSelector((state) => state.contactSettings?.item?.whatsappNumber);

  const handleHelpClick = (event: MouseEvent<HTMLElement>) => setHelpAnchor(event.currentTarget);
  const handleHelpClose = () => setHelpAnchor(null);

  const openWhatsApp = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=Hola, necesito ayuda con un trámite`,
      '_blank',
    );
    handleHelpClose();
  };

  const onCloseAccountMenu = () => setMenuElement(null);

  const openEmail = () => {
    history.push('/public/contact');
    handleHelpClose();
    onCloseAccountMenu();
  };

  const renderPublicButtons = () => (
    <>
      <NavbarNavlinkButton
        activeClassName="active"
        onClick={onCloseAccountMenu}
        to="/public/procedures/forms"
      >
        {i18next.t<string>('NAVBAR:PROCEDURES')}
      </NavbarNavlinkButton>
      <NavbarNavlinkButton
        activeClassName="active"
        onClick={onCloseAccountMenu}
        to="/public/procedures/list"
      >
        {i18next.t<string>('NAVBAR:MY_PROCEDURES')}
      </NavbarNavlinkButton>

      {/* <NavbarNavlinkButton
        activeClassName="active"
        onClick={onCloseAccountMenu}
        to="/public/procedures/contact"
      >
        {i18next.t<string>('NAVBAR:CONTACT')}
      </NavbarNavlinkButton> */}

      <Tooltip title={i18next.t<string>('NAVBAR:HELP_TOOLTIP_TITLE')}>
        <NavbarHelpButton onClick={handleHelpClick}>
          {i18next.t<string>('NAVBAR:HELP_TOOLTIP', 'Contacto')}
          <HelpOutline />
        </NavbarHelpButton>
      </Tooltip>

      <Menu
        anchorEl={helpAnchor}
        open={Boolean(helpAnchor)}
        onClose={handleHelpClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={openWhatsApp}>
          <ListItemIcon>
            <WhatsAppMenuIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="WhatsApp" />
        </MenuItem>
        <MenuItem onClick={openEmail}>
          <ListItemIcon>
            <MailOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={i18next.t<string>('NAVBAR:SEND_EMAIL', 'Enviar Correo')} />
        </MenuItem>
      </Menu>

      <NavbarLogoutButton onClick={onLogoutPublicClick}>
        {i18next.t<string>('NAVBAR:LOG_OUT')}
      </NavbarLogoutButton>
    </>
  );

  return (
    <NavbarContainer auth data-testid="navbar">
      {isPublic && !isVerified && (
        <Alert
          severity="warning"
          variant="filled"
          square
          sx={{ width: '100%', justifyContent: 'center', py: 0.5, fontSize: '0.85rem' }}
        >
          {i18next.t<string>(
            'NAVBAR:VERIFICATION_REQUIRED',
            'Debes verificar tu email para gestionar trámites.',
          )}
        </Alert>
      )}
      <NavbarContent auth isPublic={isPublic}>
        {!isPublic && <NavbarMenuButton data-testid="navbar-menu-button" onClick={onOpenMenu} />}
        {title}
        {isPublic &&
          (!isMobile ? (
            <NavbarNavlinkContainer>{renderPublicButtons()}</NavbarNavlinkContainer>
          ) : (
            <>
              <NavbarAccountButton
                aria-haspopup="true"
                data-testid="navbar-account-button"
                endIcon={<EndIcon color="inherit" />}
                isPublic
                onClick={(event: MouseEvent<HTMLButtonElement>) =>
                  setMenuElement(event.currentTarget)
                }
              >
                Menu
              </NavbarAccountButton>
              <NavbarMenu
                anchorEl={menuElement}
                isPublic
                onClose={onCloseAccountMenu}
                open={!!menuElement}
              >
                {renderPublicButtons()}
              </NavbarMenu>
            </>
          ))}
        {!isPublic && (
          <>
            <NavbarAccountButton
              aria-haspopup="true"
              data-testid="navbar-account-button"
              endIcon={<EndIcon color="action" />}
              onClick={(event: MouseEvent<HTMLButtonElement>) =>
                setMenuElement(event.currentTarget)
              }
            >
              <NavbarProfileAvatar alt={initials}>{initials}</NavbarProfileAvatar>
            </NavbarAccountButton>
            <NavbarMenu anchorEl={menuElement} onClose={onCloseAccountMenu} open={!!menuElement}>
              {items.map(({ Icon, key, label, onClick }) => (
                <NavbarMenuItem
                  data-testid={`navbar-menu-item-${key}`}
                  key={key}
                  onClick={() => {
                    onClick();
                    onCloseAccountMenu();
                  }}
                >
                  {Icon && <Icon fontSize="small" htmlColor={COLORS.grayScorpion} />}
                  <NavbarMenuItemLabel>{label}</NavbarMenuItemLabel>
                </NavbarMenuItem>
              ))}
            </NavbarMenu>
          </>
        )}
      </NavbarContent>
    </NavbarContainer>
  );
};

export default Navbar;
